import type { CrochetType, Side } from './types';
import { CROCHET_GEOMETRY } from './geometry';
import { deriveBboxFromPrimitives } from './geometry-bounds';

/** Per-type physical data: rectangle bounding box + snap anchor + snap landmarks.
 *
 *  See `docs/superpowers/specs/2026-05-05-ring-snap-ux-design.md` for landmark semantics.
 *  See `docs/superpowers/specs/2026-05-12-per-type-anchor-edit-landmarks-design.md`
 *  for anchor-edit landmark semantics. */
export type IconData = {
    bbox: { w: number; h: number };
    /** Fractional position of the icon's attach point in its bbox. (u=0..1) is tangential;
     *  (v=0..1) is radial with v=0 inner, v=1 outer. Default `(0.5, 0)` puts the attach point
     *  at the inner-edge midpoint. `RingCrochet.rotation` rotates the icon's primitives
     *  around this effective snap anchor. */
    snapAnchor: { u: number; v: number };
    /** Per-type predetermined snap landmarks: fractional positions (side, percentage)
     *  highlighted on the parent's edges during snap mode. Default = 4 edge midpoints. */
    landmarks: ReadonlyArray<{ side: Side; percentage: number }>;
    /** Discrete (u, v) points the snap-anchor edit drag will magnetically snap to.
     *  Default = 4 bbox edge midpoints + bbox center. */
    anchorEditLandmarks: ReadonlyArray<{ u: number; v: number }>;
    /** Bbox edges the snap-anchor edit drag will treat as continuous attractors.
     *  When the candidate lies near an edge in this list (and not near any
     *  anchorEditLandmark), the orthogonal coord is clamped to [0, 1] so the
     *  anchor slides along the edge. Default = all four edges. */
    anchorEditEdges: ReadonlyArray<Side>;
};

const ATTACH_INNER_MID = { u: 0.5, v: 0 };
const SLIP_STITCH_MIN_BBOX = { w: 4.4, h: 4.4 };
const CHAIN_UP_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['chain-up']);
const CHAIN_CENTER_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['chain-center']);
const SLIP_STITCH_DERIVED_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['slip-stitch']);
const SLIP_STITCH_BBOX = {
    w: Math.max(SLIP_STITCH_DERIVED_BBOX.w, SLIP_STITCH_MIN_BBOX.w),
    h: Math.max(SLIP_STITCH_DERIVED_BBOX.h, SLIP_STITCH_MIN_BBOX.h),
};
const SINGLE_CROCHET_X_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['single-crochet-x']);
const SINGLE_CROCHET_CROSSHAIR_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['single-crochet-crosshair']);
const HALF_DOUBLE_CROCHET_BBOX = deriveBboxFromPrimitives(CROCHET_GEOMETRY['half-double-crochet']);
const DEFAULT_LANDMARKS: ReadonlyArray<{ side: Side; percentage: number }> = [
    { side: 0, percentage: 0.5 },
    { side: 1, percentage: 0.5 },
    { side: 2, percentage: 0.5 },
    { side: 3, percentage: 0.5 },
];

export const DEFAULT_ANCHOR_EDIT_LANDMARKS: ReadonlyArray<{ u: number; v: number }> = [
    { u: 0.5, v: 0 },
    { u: 1,   v: 0.5 },
    { u: 0.5, v: 1 },
    { u: 0,   v: 0.5 },
    { u: 0.5, v: 0.5 },
];
export const DEFAULT_ANCHOR_EDIT_EDGES: ReadonlyArray<Side> = [0, 1, 2, 3];

export const CROCHET_ICON_DATA: Record<CrochetType, IconData> = {
    'chain-up': {
        bbox: CHAIN_UP_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
    'chain-center': {
        bbox: CHAIN_CENTER_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
    'slip-stitch': {
        bbox: SLIP_STITCH_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
    'single-crochet-x': {
        bbox: SINGLE_CROCHET_X_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
    'single-crochet-crosshair': {
        bbox: SINGLE_CROCHET_CROSSHAIR_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
    'half-double-crochet': {
        bbox: HALF_DOUBLE_CROCHET_BBOX,
        snapAnchor: ATTACH_INNER_MID,
        landmarks: DEFAULT_LANDMARKS,
        anchorEditLandmarks: DEFAULT_ANCHOR_EDIT_LANDMARKS,
        anchorEditEdges: DEFAULT_ANCHOR_EDIT_EDGES,
    },
};

/** Returns the effective snap anchor for an icon: the per-instance override if set, otherwise the type default.
 *  Accepts any structural shape with `type` and optional `snapAnchor` so callers (e.g. transform-solver helpers
 *  that take inline parameter objects) don't need to materialize a full `RingCrochet`. */
export function getSnapAnchor(
    icon: { type: CrochetType; snapAnchor?: { u: number; v: number } },
): { u: number; v: number } {
    return icon.snapAnchor ?? CROCHET_ICON_DATA[icon.type].snapAnchor;
}
